import { useEffect, useState, useRef } from 'react';
import { Button, Col, Container, Row, Stack, Form } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typeahead } from "react-bootstrap-typeahead";

import { getFileInfo, setEditing, clearEditing, updateDatabaseRecord, setScriptTags } from '../utils/Database';
import { getIndexerSignedURL } from '../utils/Storage';
import { getScripts } from "../utils/GetScripts";
import { showToast } from '../utils/Toasts';
import { formatDateTime } from '../utils/Dates';
import { getIndexInfo } from '../utils/Indexing';
import { populateListsFromView, getTagFolders } from "../utils/Views";
import { getUserInfo } from '../utils/UserUtil';
import { validateDate } from '../utils/Indexing';

import { LoadingRing } from '../components/LoadingRing';
import PdfViewer from '../components/PdfViewer';
import AltViewer from '../components/AltViewer';
import FileNameForceWrap from "../components/FileNameForceWrap";
import { UploadModal } from '../components/UploadModal';

import useUserStore from '../stores/User';
import useScriptsStore from '../stores/Scripts';

function ScriptEdit() {
    const userData = useUserStore((state) => state.data);
    const isLoading = useUserStore((state) => state.loading);
    const [isApprover, setIsApprover] = useState(userData?.isApprover || false);
    const [isCurator, setIsCurator] = useState(userData?.isCurator || false);

    const isCheckedOut = useRef(false);

    const totalScripts = useScriptsStore((state) => state.totalScripts);
    const setTotalScripts = useScriptsStore((state) => state.setTotalScripts);
    const setTotalScriptsCount = useScriptsStore((state) => state.setTotalCount);

    const { file_id } = useParams<{ file_id: string }>();
    const [fileInfo, setFileInfo] = useState<any>(null);
    const [fileType, setFileType] = useState<string>('pdf');
    const [indexedBy, setIndexedBy] = useState<any>(null);
    const [url, setUrl] = useState<string>('');
    const [isTV, setIsTV] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [validated, setValidated] = useState(false);
    const [formData, setFormData] = useState({});
    const [lists, setLists] = useState({});
    const [hasLists, setHasLists] = useState(false);
    const [tagFolders, setTagFolders] = useState<{ id: number; name: string; tags: string[] }[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('');
    const [isPilot, setIsPilot] = useState(false);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [fileChanged, setFileChanged] = useState(false);
    
    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            "Episode #": isTV ? prev["Episode #"] : '',
            [name]: typeof value === 'string' ? value : value['label']
        }));

        // Reset validation when user starts typing
        if (validated) {
            setValidated(false);
        }
    };

    // Validation function
    const validateForm = () => {
        const requiredFields = ['Script Title', 'Writer(s)'];
        const isFormValid = requiredFields.every(field => {
            const value = formData[field];
            return value && value.trim() !== '';
        });

        // Check if date is valid
        const isDateValid = validateDate(formData["Draft Date"]);

        // Check if flag_reason is valid, only if user is an approver
        const isFlagReasonValid = formData["flag_reason"].includes(',') === false || !isApprover;

        return isFormValid && isDateValid && isFlagReasonValid;
    };

    useEffect(() => {
        if (fileInfo && !fileChanged) {
            return;
        }

        const fetchData = async () => {
            const file = await getFileInfo(file_id);
            
            if (file) {
                if (file.record_deleted && !isApprover) {
                    navigate("/database"); // go to the database main page
                    return;
                }

                // Normalize the Draft Date to YYYY-MM-DD format immediately
                const normalizedDate = file["Draft Date"] 
                    ? new Date(file["Draft Date"]).toISOString().slice(0, 10)
                    : '';
                
                setFormData({
                    "Script Title": file["Script Title"] || '',
                    "Writer(s)": file["Writer(s)"] || '',
                    "Draft Date": normalizedDate,
                    "Document Type": file["Document Type"] || '',
                    "Draft": file["Draft"] || '',
                    "Alternate Titles": file["Alternate Titles"] || '',
                    "Episode #": file["Episode #"] || '',
                    "Caveat": file["Caveat"] || '',
                    "flag_reason": file["flag_reason"] || '',
                    "record_deleted": file["record_deleted"] ? 'true' : 'false',
                });

                const fileUrl = await getIndexerSignedURL(file["Title"], userData?.user_id);

                if (fileUrl) {
                    setUrl(fileUrl);
                }

                if (file["Type"] !== "application/pdf" && file["Type"] !== "PDF" && file["Type"] !== null) {
                    setFileType('alt');
                }

                if (file["indexing_id"] !== null) {
                    const indexInfo = await getIndexInfo(file["indexing_id"]);
                    if (indexInfo.index_user_id !== null) {
                        const indexUser = await getUserInfo(indexInfo.index_user_id);
                        if (indexUser) {
                            setIndexedBy(<a href={`mailto:${indexUser.email}`} target="_blank" rel="noreferrer">{indexUser.email}</a>);
                        } else {
                            setIndexedBy(<span>An Unknown User</span>);
                        }
                    }
                }

                setFileInfo(file);
                setFileChanged(false);
            } else {
                showToast("error", "Sorry, that's not a valid link.");
                navigate("/database"); // go to the database main page
            }
        }

        if (file_id) {
            fetchData();
        }
    }, [file_id, isApprover, fileChanged]);

    useEffect(() => {
        if (totalScripts.length === 0 && userData) {
            getScripts().then((data) => {
                if(data) {
                    setTotalScripts(data as []);
                    setTotalScriptsCount(data.length);
                } else {
                    showToast("error", "Unable to get scripts from database!");
                };
            });
        }
    }, [totalScripts.length, setTotalScripts, setTotalScriptsCount, userData]);

    useEffect(() => {
        async function fetchLists() {
            const [listsFromView, folders] = await Promise.all([
                populateListsFromView(['Document Type', 'Draft', 'flag_reason']),
                getTagFolders(),
            ]);
            if (listsFromView) {
                setLists(listsFromView);
                setHasLists(true);
            } else {
                showToast("error", "Unable to load lists.");
            }
            setTagFolders(folders);
        }

        if (!hasLists) {
            fetchLists();
        }
    }, [hasLists]);


    // Derive per-folder tag selections once both file and folder data are loaded
    useEffect(() => {
        if (!fileInfo || tagFolders.length === 0) return;
        const fileTags = fileInfo.tags || [];
        const catFolder = tagFolders.find(f => f.name === 'Category');
        const indFolder = tagFolders.find(f => f.name === 'Industry List');
        const yrFolder  = tagFolders.find(f => f.name === 'Year');
        const genreFolder = tagFolders.find(f => f.name === 'Genre');
        const cat = fileTags.find((t: string) => catFolder?.tags.includes(t)) || '';
        setSelectedCategory(cat);
        setSelectedIndustry(fileTags.find((t: string) => indFolder?.tags.includes(t)) || '');
        setSelectedYear(fileTags.find((t: string) => yrFolder?.tags.includes(t)) || '');
        setSelectedGenre(fileTags.find((t: string) => genreFolder?.tags.includes(t)) || '');
        setIsPilot(fileTags.includes('Pilot'));
        setIsTV(cat.toLowerCase().includes('television'));
    }, [fileInfo, tagFolders]);

    useEffect(() => {
        const checkoutRecord = async () => {
            const success = await setEditing(file_id, userData.user_id);
            if (success) {
                isCheckedOut.current = true;
            } else {
                navigate(`/database/script/${file_id}`, { replace: true }); // redirect back to view-only page
            }
        };

        if (!isCheckedOut.current) {
            checkoutRecord();
        }
    }, [file_id, userData]);

    useEffect(() => {
        if(isApprover !== userData.isApprover){
            setIsApprover(userData.isApprover);
        }
        if (isCurator !== userData.isCurator) {
            setIsCurator(userData.isCurator);
        }
    }, [userData]);

    return (
        <div className="hive-container d-flex">
            <div className="d-flex justify-content-between w-100">
                <div className="mx-sm-5 w-100 pb-1 pb-md-4">
                    {fileInfo && !isLoading ? (
                        <Container fluid className="p-3">
                            <Row className="g-3">
                                <Col lg={6} xs={12} className="pe-lg-3 d-flex flex-column">
                                    {fileInfo["Title"] && (
                                        <h1 className="fs-6 fw-bold">
                                            <FileNameForceWrap filename={fileInfo["Title"]} />
                                        </h1>
                                    )}
                                    <Form noValidate>
                                        <Form.Group as={Row}>
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Title *</Form.Label>
                                            <Col md={10} xs={12}>
                                                <Form.Control
                                                    size="sm"
                                                    name="Script Title"
                                                    className={validated
                                                        ? (!formData["Script Title"] || formData["Script Title"].trim() === '')
                                                            ? 'mt-0 is-invalid'
                                                            : 'mt-0 is-valid'
                                                        : 'input-sm mt-0'}
                                                    placeholder="The script or document title"
                                                    value={formData["Script Title"]}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    Please provide a script title.
                                                </Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">{`Writer(s) *`}</Form.Label>
                                            <Col md={10} xs={12}>
                                                <Form.Control
                                                    size="sm"
                                                    name="Writer(s)"
                                                    className={validated 
                                                        ? (!formData["Writer(s)"] || formData["Writer(s)"].trim() === '') 
                                                            ? 'mt-0 is-invalid' 
                                                            : 'mt-0 is-valid'
                                                        : 'mt-0'}
                                                    placeholder="The names of one or more writers"
                                                    value={formData["Writer(s)"]}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    Please provide the names of one or more writers.
                                                </Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Draft Date</Form.Label>
                                            <Col md={10} xs={12}>
                                                <Form.Control
                                                    size="sm"
                                                    type="date"
                                                    name="Draft Date"
                                                    className={validated
                                                        ? formData["Draft Date"] && !validateDate(formData["Draft Date"])
                                                            ? 'mt-0 w-auto is-invalid'
                                                            : formData["Draft Date"]
                                                                ? 'mt-0 w-auto is-valid'
                                                                : 'mt-0 w-auto'
                                                        : 'mt-0 w-auto'}
                                                    value={formData["Draft Date"] || ''}
                                                    onChange={handleInputChange}
                                                />
                                                {validated && formData["Draft Date"] && !validateDate(formData["Draft Date"]) && (
                                                    <div className="invalid-feedback d-block">
                                                        Draft date can't be before 1900 or in the future.
                                                    </div>
                                                )}
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Document Type</Form.Label>
                                            <Col md={4} xs={12}>
                                                <Typeahead
                                                    size="sm"
                                                    allowNew={true}
                                                    flip={true}
                                                    maxHeight="200px"
                                                    inputProps={{ className: "mt-0" }}
                                                    placeholder="Outline, Pitch Deck, etc."
                                                    selected={formData["Document Type"] ? [formData["Document Type"]] : []}
                                                    options={lists['Document Type'] ?? []}
                                                    onChange={(e) => {
                                                        e.length > 0 ?
                                                            handleInputChange({ target: { name: 'Document Type', value: e[0] } }) :
                                                            handleInputChange({ target: { name: 'Document Type', value: '' } });
                                                    }
                                                    }
                                                    onBlur={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue && (!formData["Document Type"] || formData["Document Type"] !== inputValue)) {
                                                            handleInputChange({ target: { name: 'Document Type', value: inputValue } });
                                                        }
                                                    }}
                                                />
                                            </Col>
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Version</Form.Label>
                                            <Col md={4} xs={12}>
                                                <Typeahead
                                                    size="sm"
                                                    allowNew={true}
                                                    flip={true}
                                                    maxHeight="200px"
                                                    inputProps={{ className: "mt-0" }}
                                                    placeholder="First Draft, Blue, etc."
                                                    selected={formData["Draft"] ? [formData["Draft"]] : []}
                                                    options={lists['Draft'] ?? []}
                                                    onChange={(e) => {
                                                        e.length > 0 ?
                                                            handleInputChange({ target: { name: 'Draft', value: e[0] } }) :
                                                            handleInputChange({ target: { name: 'Draft', value: '' } });
                                                    }
                                                    }
                                                    onBlur={(e) => {
                                                        const inputValue = e.target.value;
                                                        if (inputValue && (!formData["Draft"] || formData["Draft"] !== inputValue)) {
                                                            handleInputChange({ target: { name: 'Draft', value: inputValue } });
                                                        }
                                                    }}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Alt. Titles</Form.Label>
                                            <Col md={10} xs={12}>
                                                <Form.Control
                                                    size="sm"
                                                    className="mt-0 mb-3"
                                                    name="Alternate Titles"
                                                    placeholder="AKA or FKA titles, if any"
                                                    value={formData["Alternate Titles"]}
                                                    onChange={handleInputChange}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Notes</Form.Label>
                                            <Col md={10} xs={12}>
                                                <Form.Control
                                                    size="sm"
                                                    className="mt-0 mb-3"
                                                    name="Caveat"
                                                    placeholder="Any relevant notes or callouts"
                                                    value={formData["Caveat"]}
                                                    onChange={handleInputChange}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Category</Form.Label>
                                            <Col md={4} xs={12}>
                                                <Typeahead
                                                    size="sm"
                                                    flip
                                                    maxHeight="200px"
                                                    placeholder="Film, Television, etc."
                                                    inputProps={{ className: "mt-0" }}
                                                    selected={selectedCategory ? [selectedCategory] : []}
                                                    options={tagFolders.find(f => f.name === 'Category')?.tags ?? []}
                                                    onChange={(e) => {
                                                        const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                                        setSelectedCategory(val);
                                                        setIsTV(val.toLowerCase().includes('television'));
                                                        if (!val.toLowerCase().includes('television')) setIsPilot(false);
                                                    }}
                                                    onBlur={(e) => {
                                                        const v = e.target.value;
                                                        if (v && v !== selectedCategory) { setSelectedCategory(v); setIsTV(v.toLowerCase().includes('television')); }
                                                    }}
                                                />
                                            </Col>
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Industry List</Form.Label>
                                            <Col md={4} xs={12}>
                                                <Typeahead
                                                    size="sm"
                                                    flip
                                                    maxHeight="200px"
                                                    placeholder="WGA, DGA, etc."
                                                    inputProps={{ className: "mt-0" }}
                                                    selected={selectedIndustry ? [selectedIndustry] : []}
                                                    options={tagFolders.find(f => f.name === 'Industry List')?.tags ?? []}
                                                    onChange={(e) => {
                                                        const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                                        setSelectedIndustry(val);
                                                    }}
                                                    onBlur={(e) => {
                                                        const v = e.target.value;
                                                        if (v && v !== selectedIndustry) setSelectedIndustry(v);
                                                    }}
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-2">
                                            <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Yearly List</Form.Label>
                                            <Col md={4} xs={12}>
                                                <Typeahead
                                                    size="sm"
                                                    flip
                                                    maxHeight="200px"
                                                    placeholder="2024, etc."
                                                    inputProps={{ className: "mt-0" }}
                                                    selected={selectedYear ? [selectedYear] : []}
                                                    options={tagFolders.find(f => f.name === 'Year')?.tags ?? []}
                                                    onChange={(e) => {
                                                        const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                                        setSelectedYear(val);
                                                    }}
                                                    onBlur={(e) => {
                                                        const v = e.target.value;
                                                        if (v && v !== selectedYear) setSelectedYear(v);
                                                    }}
                                                />
                                            </Col>
                                            {(userData?.isApprover || (userData as any)?.isTagger || userData?.isIndexer) && (<>
                                                <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Genre</Form.Label>
                                                <Col md={4} xs={12}>
                                                    <Typeahead
                                                        size="sm"
                                                        flip
                                                        maxHeight="200px"
                                                        placeholder="Drama, Comedy, etc."
                                                        inputProps={{ className: "mt-0" }}
                                                        selected={selectedGenre ? [selectedGenre] : []}
                                                        options={tagFolders.find(f => f.name === 'Genre')?.tags ?? []}
                                                        onChange={(e) => {
                                                            const val = e.length > 0 ? String(typeof e[0] === 'string' ? e[0] : (e[0] as any).label || '') : '';
                                                            setSelectedGenre(val);
                                                        }}
                                                        onBlur={(e) => {
                                                            const v = e.target.value;
                                                            if (v && v !== selectedGenre) setSelectedGenre(v);
                                                        }}
                                                    />
                                                </Col>
                                            </>)}
                                        </Form.Group>
                                        {isTV && (
                                            <Form.Group as={Row} className="mb-2">
                                                <Form.Label as={Col} md={2} xs={6} className="col-form-label pt-0">Pilot</Form.Label>
                                                <Col md={4} xs={6}>
                                                    <Form.Check inline type="radio" id="pilot_yes" label="Yes"
                                                        checked={isPilot}
                                                        onChange={() => setIsPilot(true)}
                                                    />
                                                    <Form.Check inline type="radio" id="pilot_no" label="No"
                                                        checked={!isPilot}
                                                        onChange={() => setIsPilot(false)}
                                                    />
                                                </Col>
                                                <Form.Label as={Col} md={2} xs={6} className="col-form-label pt-0">Episode #</Form.Label>
                                                <Col md={4} xs={6}>
                                                    <Form.Control
                                                        size="sm"
                                                        className="mt-0 mb-3"
                                                        name="Episode #"
                                                        placeholder="1x01, etc."
                                                        value={formData["Episode #"]}
                                                        onChange={handleInputChange}
                                                    />
                                                </Col>
                                            </Form.Group>
                                        )}
                                        {isApprover && (
                                            <>
                                                <Form.Group as={Row} className="mb-2">
                                                    <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Flag Reason</Form.Label>
                                                    <Col md={4} xs={12}>
                                                        <Typeahead
                                                            size="sm"
                                                            allowNew={true}
                                                            placeholder={"Flag this record for review"}
                                                            flip={true}
                                                            maxHeight="200px"
                                                            inputProps={{
                                                                className: validated
                                                                    ? formData["flag_reason"] && formData["flag_reason"].trim() !== '' && !formData["flag_reason"].includes(',')
                                                                        ? 'mt-0 is-valid'
                                                                        : 'mt-0'
                                                                    : 'mt-0'
                                                            }}
                                                            selected={formData["flag_reason"] ? [formData["flag_reason"]] : []}
                                                            options={lists['flag_reason'] ?? []}
                                                            onChange={(e) => {
                                                                e.length > 0 ?
                                                                    handleInputChange({ target: { name: 'flag_reason', value: e[0] } }) :
                                                                    handleInputChange({ target: { name: 'flag_reason', value: '' } });
                                                            }
                                                            }
                                                            onBlur={(e) => {
                                                                const inputValue = e.target.value;
                                                                if (inputValue && (!formData["flag_reason"] || formData["flag_reason"] !== inputValue)) {
                                                                    handleInputChange({ target: { name: 'flag_reason', value: inputValue } });
                                                                }
                                                            }}
                                                        />
                                                        {validated && formData["flag_reason"] && formData["flag_reason"].includes(',') && (
                                                            <div className="invalid-feedback d-block">
                                                                Commas are not supported.
                                                            </div>
                                                        )}
                                                    </Col>
                                                    <Form.Label as={Col} md={2} xs={12} className="col-form-label pt-0">Deleted?</Form.Label>
                                                    <Col md={4} xs={12}>
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            id="record_deleted_yes"
                                                            name="record_deleted"
                                                            value="true"
                                                            label="Yes"
                                                            checked={formData["record_deleted"] === "true"}
                                                            onChange={() => {
                                                                handleInputChange({ target: { name: 'record_deleted', value: 'true' } })
                                                            }}
                                                        />
                                                        <Form.Check
                                                            inline
                                                            type="radio"
                                                            id="record_deleted_no"
                                                            name="record_deleted"
                                                            value="false"
                                                            label="No"
                                                            checked={formData["record_deleted"] === "false"}
                                                            onChange={() => handleInputChange({ target: { name: 'record_deleted', value: 'false' } })}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </>
                                        )}
                                        <Row className="mb-2">
                                            <Col xs={12}>
                                                <small><strong>Note:</strong> Fields marked with an asterisk * are required. All other fields are optional and can be blank.</small>
                                            </Col>
                                        </Row>
                                        <Row className="mb-2">
                                            <Col lg={6} xs={12}>
                                                <small><strong>Last Updated:</strong>{` ${formatDateTime(fileInfo["last_updated"])}`}</small>
                                            </Col>
                                            {indexedBy && (<Col lg={6} xs={12}>
                                                <small><strong>Indexed By:</strong> {indexedBy}</small>
                                            </Col>)}
                                        </Row>
                                    </Form>
                                    <Stack direction="horizontal" gap={3} className="mt-auto d-flex flex-wrap">
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                clearEditing(file_id, userData.user_id).then(() => {
                                                    isCheckedOut.current = false;
                                                    if (location.state?.from) {
                                                        navigate(-1); // go back
                                                    } else {
                                                        navigate("/database"); // go to the database main page
                                                    }
                                                });
                                            }}
                                        >
                                            <i className="bi bi-x-square me-2"></i>Cancel
                                        </Button>
                                        {(isApprover || isCurator) && (
                                            <Button
                                                variant="primary"
                                                onClick={() => setShowUploadModal(true)}
                                            >
                                                <i className="bi bi-upload me-2"></i>Upload
                                            </Button>
                                        )}
                                        <Button
                                            variant="primary ms-auto"
                                            onClick={() => {
                                                setValidated(true);

                                                if (!validateForm()) {
                                                    if (formData["Draft Date"] && !validateDate(formData["Draft Date"])) {
                                                        showToast("error", "Draft date can't be before 1900 or in the future.");
                                                    } else {
                                                        showToast("error", "Please correct any errors in the form and then save again.");
                                                    }
                                                    return;
                                                }

                                                updateDatabaseRecord(file_id, userData.user_id, formData).then(async () => {
                                                    const tagsToSave = [selectedCategory, selectedIndustry, selectedYear, selectedGenre, isPilot ? 'Pilot' : ''].filter(Boolean);
                                                    await setScriptTags(file_id, tagsToSave);
                                                    getScripts().then((data) => {
                                                        if (data) {
                                                            setTotalScripts(data as []);
                                                            setTotalScriptsCount(data.length);
                                                        }
                                                    });
                                                    setValidated(false);
                                                    if (location.state?.from === "script" || location.state?.from === "database") {
                                                        navigate(-1); // we have state, we can preserve navigation history
                                                    } else {
                                                        navigate(`/database/script/${file_id}`); // don't try to preserve history, go to view page
                                                    }
                                                });
                                            }}
                                        >
                                            <i className="bi bi-floppy2 me-2"></i>Save
                                        </Button>
                                    </Stack>
                                </Col>
                                <Col lg={6} xs={12}>
                                    <div className="border rounded overflow-hidden flex-grow-1 pdf-viewer">
                                        {url ? (
                                            fileType === 'pdf' ? (
                                                <PdfViewer fileUrl={url} fileName={fileInfo["Title"]} fileId={file_id} />
                                            ) : (
                                                <AltViewer fileName={fileInfo["Title"]} />
                                            )
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                                <i className="bi bi-file-earmark-x text-muted" style={{ fontSize: '4rem' }}></i>
                                                <h5 className="mt-3 mb-2">File Not Found</h5>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    ) : (
                        <LoadingRing />
                    )}
                </div>
            </div>
            <UploadModal
                show={showUploadModal}
                onConfirm={() => {
                    setFileChanged(true);
                    setShowUploadModal(false);
                }}
                onHide={() => setShowUploadModal(false)}
                fileInfo={fileInfo}
            />
        </div>
    );
};

export default ScriptEdit;